import React, { useState } from "react";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { alpha, darken, lighten, useTheme } from "@mui/material/styles";
import { Pencil, Check, X } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { entryVariants, resolveTransition, cssTransition } from "../motion";
import { tokens } from "@/theme/tokens";
import { AttachmentChip } from "../Attachments/AttachmentChip";
import {
  AttachmentKnowledgeChip,
  type AttachmentDocumentsApi,
} from "../Attachments/AttachmentKnowledgeChip";

export type { AttachmentDocumentsApi };

export interface UserBubbleProps {
  text: string;
  /** Files attached to this message — rendered as read-only chips above the
   *  bubble text (live on send + rehydrated from history). */
  attachments?: { id: string; name: string; mime?: string }[];
  /** Caller passes this when the message has a server-side id and the
   *  panel's editRegenerate feature flag is on. Receiving an onEdit handler
   *  is what lights up the pencil affordance; absent → read-only bubble. */
  onEdit?: (newText: string) => void | Promise<void>;
  /** When provided, each attachment chip shows where the file stands against
   *  the knowledge base: "Add to knowledge base" on a chat attachment the
   *  caller may write, "In knowledge base" on one already there (spec
   *  2026-10-05 §3.4). Attachment ids are stringified document ids. Absent →
   *  plain read-only chips. */
  attachmentDocuments?: AttachmentDocumentsApi;
  /** Poll interval while a promoted attachment is being indexed (tests). */
  pollIntervalMs?: number;
  /** Shown under a mid-turn message the turn has not read yet, so it does not
   *  read as a sent message waiting forever (ELASV1-3514). */
  queuedNote?: string;
}

/**
 * User message bubble. When `onEdit` is provided the bubble surfaces a
 * hover pencil; clicking swaps to an inline editor that calls onEdit
 * with the new text on save. Parity with ChatGPT/Claude's edit-and-
 * regenerate flow.
 */
export function UserBubble({
  text,
  attachments,
  onEdit,
  attachmentDocuments,
  pollIntervalMs,
  queuedNote,
}: UserBubbleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const editable = !!onEdit;
  const hasAttachments = !!(attachments && attachments.length);

  const reduce = useReducedMotion() ?? false;
  const theme = useTheme();

  // Render if there's text OR attachments (an attachment-only message has no
  // visible text but should still show its chips).
  if ((!text || !text.trim()) && !hasAttachments) return null;

  const save = async () => {
    const t = draft.trim();
    if (!t || !onEdit) return;
    setEditing(false);
    await onEdit(t);
  };

  return (
    <motion.div
      initial={reduce ? false : "hidden"}
      animate="visible"
      variants={entryVariants}
      transition={resolveTransition(reduce)}
      // Asymmetric spacing groups each exchange: a larger top margin separates
      // this user turn from the previous assistant reply, a small bottom margin
      // keeps it visually attached to its own answer below.
      style={{ marginTop: 22, marginBottom: 6 }}
    >
      <Box
        className="UserBubble-hoverwrap"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          alignItems: "flex-end",
          // Hover surface to reveal the pencil action.
          "&:hover .UserBubble-actions": editable
            ? { opacity: 1, pointerEvents: "auto" }
            : undefined,
        }}
      >
        {editable && !editing && (
          <Box
            className="UserBubble-actions"
            sx={{
              display: "flex",
              alignItems: "center",
              mr: 1,
              opacity: 0,
              pointerEvents: "none",
              transition: cssTransition.hover,
            }}
          >
            <Tooltip title="Edit message">
              <IconButton
                size="small"
                onClick={() => {
                  setDraft(text);
                  setEditing(true);
                }}
                aria-label="edit message"
                sx={{
                  bgcolor: "action.hover",
                  "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.12) },
                }}
              >
                <Pencil size={14} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {editing ? (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end", maxWidth: "80%" }}>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              multiline
              maxRows={8}
              autoFocus
              size="small"
              sx={{ minWidth: 320 }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  setEditing(false);
                }
              }}
            />
            <Tooltip title="Save & resend">
              <IconButton size="small" onClick={save} aria-label="save edit">
                <Check size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton size="small" onClick={() => setEditing(false)} aria-label="cancel edit">
                <X size={14} />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box
            sx={{
              maxWidth: "78%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.75,
            }}
          >
            {hasAttachments && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
                {attachments!.map((a) =>
                  attachmentDocuments && Number.isInteger(Number(a.id)) ? (
                    <AttachmentKnowledgeChip
                      key={a.id}
                      documentId={Number(a.id)}
                      name={a.name}
                      mime={a.mime}
                      api={attachmentDocuments}
                      pollIntervalMs={pollIntervalMs}
                    />
                  ) : (
                    <AttachmentChip key={a.id} name={a.name} mime={a.mime} status="ready" />
                  ),
                )}
              </Box>
            )}
            {text && text.trim() && (
              <Box
                sx={{
                  px: 2.25,
                  py: 1.45,
                  // Flat bubble: same off-page surface as the composer (a touch
                  // darker in light, lighter in dark) so it reads as a distinct
                  // bubble on the page — no shadow, no border.
                  bgcolor:
                    theme.palette.mode === "light"
                      ? darken(theme.palette.background.default, 0.035)
                      : lighten(theme.palette.background.default, 0.06),
                  color: "text.primary",
                  borderRadius: `${tokens.radius.bubble}px`,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                  {text}
                </Typography>
              </Box>
            )}
            {queuedNote && (
              <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                {queuedNote}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </motion.div>
  );
}
